import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { RENDER_TYPE, SCREEN_STATE } from '../base';
import { IProps } from '../docReader';
import { Outlines } from '../services/useUpdateDocReaderOutline';

type MergeAction = {
    type: 'merge';
} & Partial<DocReaderState>;
type PagePrevNextAction = {
    type: 'pagePrev' | 'pageNext';
};
type PageJumpAction = {
    type: 'pageJump';
    pageNum: number;
};
type UpdateDisplayPageNumAction = {
    type: 'updateDisplayPageNum';
    pageNum: number;
};
type UpdatePdfOutlines = {
    type: 'updatePdfOutlines';
    pdfOutlines: Outlines;
};
type ZoomInOutAction = {
    type: 'zoomIn' | 'zoomOut';
    delta?: number;
};
type ZoomResetAction = {
    type: 'zoomReset';
};
type ZoomSetAction = {
    type: 'zoomSet';
    scale: number;
};
type FullScreenInOutAction = {
    type: 'fullScreenIn' | 'fullScreenOut' | 'fullScreenToggle';
};
type OutlineSidebarAction = {
    type: 'outlineSidebarShow' | 'outlineSidebarHide' | 'outlineSidebarToggle';
};
type ToolBarAction = {
    type: 'toolbarShow' | 'toolbarHide' | 'toolbarToggle';
};
type FlippingModeAction = {
    type: 'flippingModeVertical' | 'flippingModeHorizontal';
};
type RenderByAction = {
    type: 'renderBySvg' | 'renderByCanvas' | 'renderByImage';
};
type RerenderAction = {
    type: 'rerender';
};
type NetworkErrorAction = {
    type: 'networkError';
};

type ActionTypes = {
    merge: MergeAction;
    pagePrev: PagePrevNextAction;
    pageNext: PagePrevNextAction;
    pageJump: PageJumpAction;
    updateDisplayPageNum: UpdateDisplayPageNumAction;
    updatePdfOutlines: UpdatePdfOutlines;
    zoomIn: ZoomInOutAction;
    zoomOut: ZoomInOutAction;
    zoomReset: ZoomResetAction;
    zoomSet: ZoomSetAction;
    fullScreenIn: FullScreenInOutAction;
    fullScreenOut: FullScreenInOutAction;
    fullScreenToggle: FullScreenInOutAction;
    outlineSidebarShow: OutlineSidebarAction;
    outlineSidebarHide: OutlineSidebarAction;
    outlineSidebarToggle: OutlineSidebarAction;
    toolbarShow: ToolBarAction;
    toolbarHide: ToolBarAction;
    toolbarToggle: ToolBarAction;
    flippingModeVertical: FlippingModeAction;
    flippingModeHorizontal: FlippingModeAction;
    renderBySvg: RenderByAction;
    renderByCanvas: RenderByAction;
    renderByImage: RenderByAction;
    rerender: RerenderAction;
    networkError: NetworkErrorAction;
};
type DocReaderAction = ActionTypes[keyof ActionTypes];

// 类型化 Dispatch
type DocReaderDispatch<ActionType extends keyof ActionTypes> = (
    action: {
        type: ActionType;
    } & ActionTypes[ActionType],
) => void;

type PageFlipMode = 'vertical' | 'horizontal';

export type DocReaderState = {
    networkError: boolean;
    renderType: typeof RENDER_TYPE[keyof typeof RENDER_TYPE];
    reRender: boolean;
    pageNum: {
        value: number;
    };
    displayPageNum: number; // 当前展示的页码
    totalPages: number; // 总页数
    pageChangeCallback: (targetPage: number, allPageNum: number) => void; // 页码变化回调
    screenCallback: (isFullScren: boolean) => void;
    screenState: SCREEN_STATE;
    scale: number;
    initScale: number;
    lastScale: number;
    isError: boolean;
    isRendered: boolean;
    isShowBar: boolean;
    loading: boolean;
    pdf: any;
    pageFlipMode: PageFlipMode;
    pdfOutlines: Outlines;
    showOutlines: boolean;

    // Refs
    pdfContentRef: React.RefObject<HTMLDivElement>;
    canvasRef: React.RefObject<HTMLCanvasElement>;
    svgContainerRef: React.RefObject<HTMLDivElement>;
    docContainerRef: React.RefObject<HTMLDivElement>;
    // props
    // 这里是为了在尽可能少改动旧代码的情况下抽离渲染逻辑。不建议如此设计
    docReaderProps: IProps;
};

const initialDocReader: DocReaderState = {
    networkError: false,
    renderType: RENDER_TYPE.IMAGE,
    reRender: false,
    pageNum: {
        value: 0,
    },
    displayPageNum: 0,
    totalPages: 0, // 总页数
    screenCallback: () => {
        return;
    },
    pageChangeCallback: () => {
        return;
    },
    screenState: 'no-full',
    isError: false,
    scale: 0,
    initScale: 1,
    lastScale: 1,
    isRendered: false, // 控制水印渲染
    isShowBar: true,
    loading: true,
    pdf: { numPages: '-' },
    pageFlipMode: 'vertical',
    pdfOutlines: [],
    showOutlines: true,

    pdfContentRef: React.createRef(),
    canvasRef: React.createRef(),
    svgContainerRef: React.createRef(),
    docContainerRef: React.createRef(),
    docReaderProps: {
        resourceUrl: '',
        documentTitle: '',
    },
};

const ZOOM_STEP = 0.2;

function DocReaderReducer(DocReader: DocReaderState, action: DocReaderAction): DocReaderState {
    const { pageNum, totalPages, loading, pageChangeCallback, screenCallback } = DocReader;
    const { type } = action;

    /**
     * 跳页并出发跳页回调
     * @param targetPage aim page number
     * @returns
     */
    const safePageNumber = (targetPage: number) => {
        let target = targetPage;
        // 页码范围限制
        if (target < 1) {
            target = 1;
        }
        if (target > totalPages) {
            target = totalPages;
        }
        if (!target || target === pageNum.value || loading) {
            return pageNum.value;
        }

        return target;
    };

    const safeScale = (scale: number) => {
        // 最小缩小到20%, 最大放大到500%
        if (scale < 0.2) {
            return 0.2;
        }
        if (scale > 5) {
            return 5;
        }
        return scale;
    };

    /**
     * 获取下一个缩放比例
     * @param delta 缩放比例
     * @returns
     */
    const calculateScale = (delta: number) => {
        const { scale } = DocReader;
        let ret = scale + delta;
        const fixed = Number(scale.toFixed(2));
        if (scale !== fixed) {
            ret = fixed + (delta > 0 ? 0 : delta);
        }
        ret = Number(ret.toFixed(2));
        // 防止出现溢出
        return safeScale(ret);
    };

    switch (type) {
        // 这里是为了方便从 Class Component 迁移到 Function Component 时，不需要修改太多代码
        // 不建议在 Reducer 中使用这种方式整合数据
        case 'merge': {
            const merged = {
                ...DocReader,
                ...action,
            };
            return merged;
        }

        // 翻页
        case 'pageNext': {
            return {
                ...DocReader,
                pageNum: { value: safePageNumber(pageNum.value + 1) },
            };
        }
        case 'pagePrev': {
            return {
                ...DocReader,
                pageNum: { value: safePageNumber(pageNum.value - 1) },
            };
        }
        case 'pageJump': {
            return {
                ...DocReader,
                pageNum: { value: safePageNumber(action.pageNum) },
            };
        }

        case 'updateDisplayPageNum': {
            const targetPageNum = safePageNumber(action.pageNum);
            // 触发跳页回调
            if (pageChangeCallback) {
                pageChangeCallback(targetPageNum, totalPages);
            }
            return {
                ...DocReader,
                displayPageNum: targetPageNum,
            };
        }

        case 'updatePdfOutlines': {
            return {
                ...DocReader,
                pdfOutlines: action.pdfOutlines || [],
            };
        }

        // 缩放
        case 'zoomIn': {
            return {
                ...DocReader,
                scale: calculateScale(action.delta || ZOOM_STEP),
            };
        }
        case 'zoomOut': {
            return {
                ...DocReader,
                scale: calculateScale(-(action.delta || ZOOM_STEP)),
            };
        }
        case 'zoomReset': {
            return {
                ...DocReader,
                scale: 0,
            };
        }
        case 'zoomSet': {
            return {
                ...DocReader,
                scale: safeScale(action.scale),
            };
        }

        // 全屏
        case 'fullScreenIn': {
            return {
                ...DocReader,
                screenState: 'full',
                pageNum: { value: DocReader.displayPageNum },
                isRendered: false,
            };
        }
        case 'fullScreenOut': {
            return {
                ...DocReader,
                screenState: 'no-full',
                pageNum: { value: DocReader.displayPageNum },
                isRendered: false,
            };
        }
        case 'fullScreenToggle': {
            const nextScreenState = DocReader.screenState === 'full' ? 'no-full' : 'full';
            screenCallback(nextScreenState === 'full');
            return {
                ...DocReader,
                screenState: nextScreenState,
                pageNum: { value: DocReader.displayPageNum },
                isRendered: false,
            };
        }

        case 'outlineSidebarShow': {
            return {
                ...DocReader,
                showOutlines: true,
            };
        }
        case 'outlineSidebarHide': {
            return {
                ...DocReader,
                showOutlines: false,
            };
        }
        case 'outlineSidebarToggle': {
            return {
                ...DocReader,

                showOutlines: !DocReader.showOutlines,
            };
        }

        // 工具栏
        case 'toolbarShow': {
            return {
                ...DocReader,
                isShowBar: true,
            };
        }
        case 'toolbarHide': {
            if (DocReader.screenState !== 'full') {
                return {
                    ...DocReader,
                    isShowBar: true,
                };
            }
            return {
                ...DocReader,
                isShowBar: false,
            };
        }
        case 'toolbarToggle': {
            if (DocReader.screenState !== 'full') {
                return {
                    ...DocReader,
                    isShowBar: true,
                };
            }
            return {
                ...DocReader,
                isShowBar: !DocReader.isShowBar,
            };
        }

        // 切换翻页模式
        case 'flippingModeVertical': {
            return {
                ...DocReader,
                pageFlipMode: 'vertical',
                isRendered: false,
            };
        }
        case 'flippingModeHorizontal': {
            return {
                ...DocReader,
                pageFlipMode: 'horizontal',
                isRendered: false,
            };
        }

        // 切换渲染器
        case 'renderBySvg': {
            return {
                ...DocReader,
                renderType: RENDER_TYPE.SVG,
                reRender: true,
            };
        }
        case 'renderByCanvas': {
            return {
                ...DocReader,
                renderType: RENDER_TYPE.CANVAS,
                reRender: true,
            };
        }
        case 'renderByImage': {
            return {
                ...DocReader,
                renderType: RENDER_TYPE.IMAGE,
                reRender: true,
            };
        }

        // 重新渲染
        case 'rerender': {
            return {
                ...DocReader,
                reRender: true,
            };
        }

        // 网络错误
        case 'networkError': {
            return {
                ...DocReader,
                networkError: true,
                loading: false,
            };
        }

        default: {
            console.error('Unknown action: ' + type);
            return DocReader;
        }
    }
}

export const DocReaderContext = createContext<DocReaderState | null>(initialDocReader);

export const DocReaderDispatchContext = createContext<DocReaderDispatch<keyof ActionTypes> | null>(null);

interface DocReaderProviderProps {
    children: ReactNode;
}

export function DocReaderProvider({ children }: DocReaderProviderProps) {
    const [docReaderState, dispatch] = useReducer(DocReaderReducer, initialDocReader);

    return (
        <DocReaderContext.Provider value={docReaderState}>
            <DocReaderDispatchContext.Provider value={dispatch}>{children}</DocReaderDispatchContext.Provider>
        </DocReaderContext.Provider>
    );
}

export function useDocReader(): [DocReaderState, (state: Partial<DocReaderState>, callback?: () => void) => void] {
    const dispatch = useDocReaderDispatch();
    const mergeContext = (state: Partial<DocReaderState>, callback?: () => void) => {
        dispatch?.({ type: 'merge', ...state });
        callback?.();
    };
    return [useContext(DocReaderContext) as DocReaderState, mergeContext];
}

export function useDocReaderDispatch(): DocReaderDispatch<keyof ActionTypes> {
    return useContext(DocReaderDispatchContext) as DocReaderDispatch<keyof ActionTypes>;
}
